import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import AddIcon from "@mui/icons-material/Add";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import FileSaver from "file-saver";
import { AuthState } from "context/AuthContext";
import { SbtRoleAccess } from "data/storeBudgetData";

const formatCurrency = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

const formatDisplayDate = (value) => {
  if (!value) return "-";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = String(value.getDate()).padStart(2, "0");
    const month = String(value.getMonth() + 1).padStart(2, "0");
    return `${day}-${month}-${value.getFullYear()}`;
  }
  const datePart = String(value).split("T")[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${parsed.getFullYear()}`;
};

const ROW_HEIGHT = 56;
const HEADER_HEIGHT = 56;
const FOOTER_HEIGHT = 48;
const MIN_VISIBLE_ROWS = 2;
const BODY_MIN_HEIGHT = ROW_HEIGHT * MIN_VISIBLE_ROWS;

const listGridSx = {
  border: "none",
  height: "auto",
  minHeight: HEADER_HEIGHT + BODY_MIN_HEIGHT + FOOTER_HEIGHT,
  maxHeight: 520,
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#dbeafe",
    fontWeight: 600,
    borderRight: "1px solid #bfdbfe",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  "& .MuiDataGrid-cell": {
    borderColor: "#eceff1",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  "& .MuiDataGrid-columnHeader[data-field='project_id'], & .MuiDataGrid-cell[data-field='project_id']": {
    position: "sticky",
    left: 0,
    zIndex: 3,
    backgroundColor: "#fff",
    boxShadow: "2px 0 6px rgba(15, 23, 42, 0.08)",
  },
  "& .MuiDataGrid-columnHeader[data-field='project_id']": {
    backgroundColor: "#dbeafe",
    zIndex: 4,
  },
  "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='project_id']": {
    backgroundColor: "#f8fbff",
  },
  "& .MuiDataGrid-row:hover": { backgroundColor: "#f8fbff" },
  "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #eceff1", minHeight: FOOTER_HEIGHT },
  "& .MuiDataGrid-virtualScroller": {
    minHeight: BODY_MIN_HEIGHT,
    maxHeight: 400,
  },
  "& .MuiDataGrid-overlay": { minHeight: BODY_MIN_HEIGHT },
};

export default function ListStoreBudget() {
  const [budgetList, setBudgetList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
  const navigate = useNavigate();
  const { user } = AuthState();
  const roleData = SbtRoleAccess(user);

  const handleDownload = useCallback(
    async (e, row) => {
      e.stopPropagation();
      try {
        setLoadingOpen(true);
        const res = await pythonVmInstance({
          url: `/store-budget-tracker/report/download-store-budget?project_id=${row?.project_id}`,
          headers: { "Content-Type": "application/json" },
          method: "GET",
          responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        FileSaver.saveAs(url, `${row?.project_id}_store_budget.csv`);
      } catch (error) {
        setSnackBarOpen(true);
        setSnackBar({
          ...snackBar,
          message: "Failed to download file",
          severity: "error",
          anchorOrigin: { horizontal: "center", vertical: "top" },
        });
      } finally {
        setLoadingOpen(false);
      }
    },
    [setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar]
  );

  const columns = useMemo(
    () =>
      [
        {
          field: "project_id",
          headerName: "Project Id",
          minWidth: 260,
          flex: 1.4,
          renderCell: ({ row }) => (
            <Tooltip title={row?.project_id || ""} placement="top-start">
              <Typography
                variant="body2"
                noWrap
                sx={{ width: "100%", fontWeight: 600, textAlign: "center" }}
              >
                {row?.project_id || "-"}
              </Typography>
            </Tooltip>
          ),
        },
        { field: "store_name", headerName: "Store Name", minWidth: 180, flex: 1.3 },
        { field: "store_code", headerName: "Store Code", minWidth: 120, flex: 0.8 },
        { field: "channel", headerName: "Channel", minWidth: 110, flex: 0.7 },
        { field: "project", headerName: "Project", minWidth: 130, flex: 0.9 },
        { field: "city", headerName: "City", minWidth: 120, flex: 0.8 },
        { field: "state", headerName: "State", minWidth: 120, flex: 0.8 },
        { field: "work_type", headerName: "Work Type", minWidth: 130, flex: 0.8 },
        {
          field: "store_opening_date",
          headerName: "Opening Date",
          minWidth: 140,
          flex: 0.85,
          valueGetter: (value, row) => row?.store_opening_date || value || "",
          renderCell: ({ row }) => (
            <Typography variant="body2" noWrap sx={{ width: "100%", textAlign: "center" }}>
              {formatDisplayDate(row?.store_opening_date)}
            </Typography>
          ),
          sortComparator: (a, b) => String(a || "").localeCompare(String(b || "")),
        },
        {
          field: "carpet_area",
          headerName: "Carpet Area",
          minWidth: 130,
          flex: 0.8,
          type: "number",
        },
        {
          field: "total_budget",
          headerName: "Total Budget",
          minWidth: 150,
          flex: 1,
          type: "number",
          renderCell: ({ row }) => formatCurrency(row?.total_budget),
        },
        {
          field: "is_submitted",
          headerName: "Status",
          minWidth: 110,
          flex: 0.7,
          valueGetter: (value, row) => (row?.status === "submitted" ? "Closed" : "Open"),
          renderCell: ({ row }) => {
            const submitted = row?.status === "submitted";
            return (
              <Chip
                label={submitted ? "Closed" : "Open"}
                color={submitted ? "success" : "warning"}
                size="small"
                variant="outlined"
              />
            );
          },
        },
        {
          field: "action",
          headerName: "Action",
          width: 90,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Tooltip title="Download CSV">
              <IconButton size="small" onClick={(e) => handleDownload(e, params?.row)}>
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      ].map((col) => ({
        headerAlign: "center",
        align: "center",
        sortable: col.sortable !== false,
        ...col,
      })),
    [handleDownload]
  );

  const handleCreateBudgetClick = () => {
    navigate(`/explore/store_budget_tracker/create_store_budget`);
  };

  const handleRowClick = (row) => {
    navigate(`/explore/store_budget_tracker/create_store_budget`, {
      state: { data: row },
    });
  };

  const fetchBudgets = async () => {
    try {
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/store-budget`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      setBudgetList((res?.data || []).map((row, i) => ({ ...row, id: row?.id || `budget-${i}` })));
    } catch {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Failed to fetch store budgets",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      setLoadingOpen(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return budgetList;
    return budgetList.filter((row) =>
      [row?.project_id, row?.store_name, row?.store_code, row?.channel, row?.project, row?.city, row?.state, row?.work_type, formatDisplayDate(row?.store_opening_date)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [budgetList, searchText]);

  const openCount = budgetList.filter((row) => row?.status !== "submitted").length;
  const closedCount = budgetList.length - openCount;

  return (
    <Box padding={2}>
      <Stack padding={2} boxShadow="0px 1px 3px rgba(0, 0, 0, 0.2)" borderRadius={2} spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5}>
          <Box>
            <Typography variant="h6">Store Budgets</Typography>
            <Typography variant="body2" color="text.secondary">
              Open a row to view or update the budget. Download exports a CSV for that project.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Chip label={`${budgetList.length} Total`} variant="outlined" />
            <Chip label={`${openCount} Open`} color="warning" variant="outlined" />
            <Chip label={`${closedCount} Closed`} color="success" variant="outlined" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateBudgetClick}
              disabled={roleData?.roles === "VIEWER"}
            >
              Create Store Budget
            </Button>
          </Stack>
        </Stack>

        <TextField
          size="small"
          placeholder="Search project, store, city, channel..."
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} fontSize="small" />,
          }}
          sx={{ maxWidth: 360 }}
        />

        <Box sx={{ width: "100%", minHeight: HEADER_HEIGHT + BODY_MIN_HEIGHT + FOOTER_HEIGHT, maxHeight: 520, overflowX: "auto", overflowY: "hidden", border: "1px solid #e5e7eb", borderRadius: 1.5 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            autoHeight
            getRowId={(row) => row.id}
            rowHeight={ROW_HEIGHT}
            columnHeaderHeight={HEADER_HEIGHT}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            onRowClick={(params) => handleRowClick(params?.row)}
            localeText={{ noRowsLabel: "No store budgets found" }}
            sx={listGridSx}
          />
        </Box>
      </Stack>
    </Box>
  );
}
